import { SkeletonLoader } from '../../design-system/components/SkeletonLoader';
import { useTaskRequest } from '../../api/hooks/useTaskRequest';
import { plannedPlaces, roleLabel } from './task-planned-places';
import type { TaskState } from '../../api/rmf-client';
import './TaskPhasesList.css';

/**
 * "Where this task was asked to go", for a task at ANY status.
 *
 * The companion to `TaskPhasesList`, which shows where RMF confirms it
 * WENT. A queued task has visited nothing, so that list is empty and used
 * to be all the drawer had -- an operator could not see where a queued
 * task was headed until it became active.
 *
 * Deliberately NOT merged with the visited list: the two can disagree
 * (RMF replans; a clean's go-to resolves elsewhere) and reconciling them
 * would be inventing an answer. Same styling, so they read as one system.
 */
export function TaskPlannedList({ task }: { task: TaskState }) {
  const query = useTaskRequest(task.id);

  if (query.isLoading) {
    return <SkeletonLoader shape="text-line" count={1} />;
  }
  if (query.isError) {
    return (
      <p className="nx-task-phases__empty">
        Could not load the dispatch request for this task.
      </p>
    );
  }

  // THREE STATES, NOT TWO. This used to choose the empty branch on
  // `places.length` alone and never consult `query.data`, so a 200
  // carrying the whole stored request rendered the same sentence as a
  // 404 -- while `query.data.request.phases[0].params.zone` sat in the
  // same component naming the zone. A clean whose projected category is
  // not the literal 'clean' does exactly that: `plannedPlaces('custom',
  // req)` is `[]` for the identical request `plannedPlaces('clean',
  // req)` extracts a zone from, and every clean dispatched before the
  // worker started labelling categories reads as `custom`.
  // THE STORED REQUEST'S OWN CATEGORY WINS over the live projection's.
  // `task.type` comes from RMF's published state, and for a QUEUED task
  // RMF publishes the request's own header -- `compose` -- which
  // `task_category` normalises to `custom` whenever the
  // `nexsoss:category=` label round-trip fails. That is the ordinary
  // state at any site running a worker older than the one that started
  // labelling dispatches, and this platform is deployed one image at a
  // time. Measured on the exact request the backend stores:
  // `plannedPlaces('custom', req)` and `plannedPlaces('patrol', req)`
  // are both `[]` where `plannedPlaces(req.category, req)` yields the
  // zone. The authoritative category is in the response we just
  // fetched, so read it from there.
  const places = plannedPlaces(query.data?.request?.category ?? task.type, query.data?.request);
  if (!query.data) {
    return (
      <p className="nx-task-phases__empty" data-testid="task-planned-empty">
        {/* NO CAUSE CLAIMED. A 404 here covers an RMF-originated task,
            a dispatch whose worker reply was lost (the task runs, the
            correlation is never written), an id re-minted by the
            dispatch or lift-boarding retry, and an api-server that
            predates the route -- and the SPA and api-server roll
            separately, so the last one is a live possibility during
            every deploy. "It was not dispatched from this console" was
            true of one of the four. */}
        No dispatch request on file for this task.
      </p>
    );
  }
  if (places.length === 0) {
    return (
      <p className="nx-task-phases__empty" data-testid="task-planned-empty">
        The dispatch request is on file, but it names no waypoints for this
        task type.
      </p>
    );
  }

  return (
    <>
      <ol className="nx-task-phases" data-testid="task-planned-list">
        {places.map((p, i) => (
          <li
            key={`${p.role}-${p.place}-${i}`}
            className="nx-task-phases__item nx-task-phases__item--completed"
          >
            <span className="nx-task-phases__icon" aria-hidden="true">
              ◎
            </span>
            <span className="nx-task-phases__label">
              {[roleLabel(p), p.place].filter(Boolean).join(' ')}
            </span>
          </li>
        ))}
      </ol>
      {query.data?.requested_via === 'calendar' ? (
        <p className="nx-task-phases__empty">From the schedule.</p>
      ) : null}
    </>
  );
}
